import {useQuery} from '@tanstack/react-query';
import React from 'react';

import {isRunInFlight} from 'app/components/AgentExplorer/types';
import {api} from 'app/modules/Remote';
import {useStateWithDeps} from 'app/utils/hookUtils';
import * as routeUtils from 'app/utils/routeUtils';

import {
  ApiRecentRun,
  PendingAgentRunsContext,
  PendingBulkRun,
  PendingRun,
  RECENT_RUNS_QUERY_KEY,
} from './usePendingAgentRuns';

const POLL_INTERVAL_MS = 15_000;

/**
 * Tracks the current user's in-flight agent explore runs via one recent-runs
 * query that hydrates at app load and polls only while something is in flight.
 */
export const PendingAgentRunsProvider: React.FC<
  React.PropsWithChildren<{
    firebaseUid: string | null;
    organizationId?: string | null;
    canRunAgent?: boolean;
  }>
> = ({firebaseUid, organizationId = null, canRunAgent = false, children}) => {
  // Keyed to the session so a user/org switch starts a fresh registry.
  const [pendingRuns, setPendingRuns] = useStateWithDeps<ReadonlyMap<number, PendingRun>>(
    new Map(),
    [firebaseUid, organizationId]
  );

  const addPendingRun = React.useCallback(
    (run: PendingRun) => {
      setPendingRuns((prev) => new Map(prev).set(run.runId, run));
    },
    [setPendingRuns]
  );

  const removePendingRun = React.useCallback(
    (runId: number) => {
      setPendingRuns((prev) => {
        if (!prev.has(runId)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(runId);
        return next;
      });
    },
    [setPendingRuns]
  );

  const [pendingBulkRuns, setPendingBulkRuns] = useStateWithDeps<
    ReadonlyMap<number, PendingBulkRun>
  >(new Map(), [firebaseUid, organizationId]);

  const addPendingBulkRun = React.useCallback(
    (workflowId: number, workflowRunId: number, title?: string) => {
      setPendingBulkRuns((prev) =>
        new Map(prev).set(workflowRunId, {
          workflowRunId,
          title,
          url: organizationId
            ? routeUtils.agentWorkflowRunUrl(organizationId, workflowId, workflowRunId)
            : undefined,
        })
      );
    },
    [organizationId, setPendingBulkRuns]
  );

  const removePendingBulkRun = React.useCallback(
    (workflowRunId: number) => {
      setPendingBulkRuns((prev) => {
        if (!prev.has(workflowRunId)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(workflowRunId);
        return next;
      });
    },
    [setPendingBulkRuns]
  );

  const {data: recentRuns, isFetching} = useQuery({
    queryKey: [RECENT_RUNS_QUERY_KEY, firebaseUid, organizationId],
    queryFn: async () => {
      const response = await api.agent.getRecentRuns();
      return response.toJS().data as ApiRecentRun[];
    },
    enabled: firebaseUid !== null && canRunAgent,
    refetchInterval: pendingRuns.size > 0 || pendingBulkRuns.size > 0 ? POLL_INTERVAL_MS : false,
    staleTime: POLL_INTERVAL_MS,
    refetchOnWindowFocus: false,
  });

  // Reconcile against fresh poll data: adopt unknown in-flight runs (boot
  // hydration, other devices or tabs) and drop absent (archived) ones — unless
  // the data is mid-refresh and may predate a just-started run.
  React.useEffect(() => {
    if (!recentRuns) {
      return;
    }
    setPendingRuns((prev) => {
      let changed = false;
      const next = new Map(prev);
      const responseIds = new Set(recentRuns.map((run) => run.id));
      for (const run of recentRuns) {
        if (!next.has(run.id) && isRunInFlight(run.status)) {
          next.set(run.id, {runId: run.id});
          changed = true;
        }
      }
      if (!isFetching) {
        for (const runId of next.keys()) {
          if (!responseIds.has(runId)) {
            next.delete(runId);
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [recentRuns, isFetching, setPendingRuns]);

  // Adopt batches the poll turns up, which is how one started on another device or before a reload
  // gets tracked. Absence prunes nothing here, unlike above: a batch registered at kickoff has no
  // runs of its own to be found yet, so it stays until its status settles.
  React.useEffect(() => {
    if (!recentRuns) {
      return;
    }
    setPendingBulkRuns((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const run of recentRuns) {
        const {agentWorkflowRunId, agentWorkflowId, agentWorkflowRunStatus} = run;
        if (
          agentWorkflowRunId === null ||
          agentWorkflowId === null ||
          agentWorkflowRunStatus === null ||
          !isRunInFlight(agentWorkflowRunStatus) ||
          next.has(agentWorkflowRunId)
        ) {
          continue;
        }
        next.set(agentWorkflowRunId, {
          workflowRunId: agentWorkflowRunId,
          title: run.agentWorkflowTitle ?? undefined,
          url: organizationId
            ? routeUtils.agentWorkflowRunUrl(organizationId, agentWorkflowId, agentWorkflowRunId)
            : undefined,
        });
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [recentRuns, organizationId, setPendingBulkRuns]);

  const value = React.useMemo(
    () => ({
      pendingRuns,
      addPendingRun,
      removePendingRun,
      pendingBulkRuns,
      addPendingBulkRun,
      removePendingBulkRun,
      recentRuns,
    }),
    [
      pendingRuns,
      addPendingRun,
      removePendingRun,
      pendingBulkRuns,
      addPendingBulkRun,
      removePendingBulkRun,
      recentRuns,
    ]
  );

  return (
    <PendingAgentRunsContext.Provider value={value}>{children}</PendingAgentRunsContext.Provider>
  );
};
